import { createMachine } from 'xstate';

import shared from 'common/bento/shared';
import { AnyMachineEvent } from 'common/bento/types/Abstract';
import { BentoModuleDoneStatus } from 'common/bento/types/BentoModule';

import locales from '../../../locales';
import actions from './actions';
import guards, { Guard } from './guards';
import model, { Context } from './model';
import services, { Service } from './services';
import { State } from './state';

const machine = createMachine<Context, AnyMachineEvent>(
  {
    context: model.initialContext,

    id: 'capital-deposit-mandatee',

    initial: State.INIT,

    on: {
      '*': {
        actions: shared.actions.unhandled(),
      },
    },

    states: {
      [State.INIT]: {
        always: [
          {
            cond: Guard.ShouldCreateCompany,
            target: State.COMPANY_NAME,
          },
          {
            cond: Guard.ShouldUploadMandate,
            target: State.MANDATE,
          },
          {
            cond: Guard.ShouldFillHasEuBankAccount,
            target: State.COMPANY_INFORMATION_INTRO,
          },
          {
            cond: Guard.ShouldAnswerCompanyCompliance,
            target: State.COMPANY_COMPLIANCE,
          },
          {
            cond: Guard.ShouldAnswerCapitalDepositQuestions,
            target: State.CAPITAL_DEPOSIT_QUESTIONS,
          },
          {
            cond: Guard.ShouldRegisterBeneficiaries,
            target: State.BENEFICIARIES,
          },
          {
            cond: Guard.ShouldSubmitApplication,
            target: State.VERIFICATION,
          },
          { target: State.CAPITAL_DEPOSIT_NEXT_STEPS },
        ],
      },

      [State.COMPANY_NAME]: {
        entry: shared.actions.trackView(),
        on: {
          PREV: State.ABORT,
          SUBMIT_COMPANY_NAME: [
            {
              cond: Guard.ShouldCreateCompany,
              target: State.CAPITAL_DEPOSIT_CREATE,
            },
            { target: State.UPDATE_COMPANY_NAME },
          ],
        },
      },

      [State.CAPITAL_DEPOSIT_CREATE]: {
        invoke: {
          onDone: {
            // Note that if the company entity is created, the whole onboarding will be unmounted:
            // calling setCompanyContext will make some hooks in <SecuredRoutes/> call queries with the new companyProfileId
            // thus rendering a loader instead
            target: State.MANDATE,
          },
          onError: {
            actions: shared.actions.error(),
            target: State.COMPANY_NAME,
          },
          src: Service.CreateCapitalDeposit,
        },
      },

      [State.UPDATE_COMPANY_NAME]: {
        invoke: {
          onDone: {
            target: State.MANDATE,
          },
          onError: {
            actions: shared.actions.error(),
            target: State.COMPANY_NAME,
          },
          src: Service.UpdateCompanyName,
        },
      },

      [State.MANDATE]: {
        on: {
          NEXT: State.COMPANY_INFORMATION_INTRO,
          PREV: State.COMPANY_NAME,
        },
      },

      [State.COMPANY_INFORMATION_INTRO]: {
        entry: shared.actions.trackView(),
        on: {
          NEXT: State.EU_BANK_ACCOUNT,
          PREV: State.MANDATE,
        },
      },

      [State.EU_BANK_ACCOUNT]: {
        on: {
          NEXT: State.COMPANY_COMPLIANCE,
          PREV: State.COMPANY_INFORMATION_INTRO,
        },
      },

      [State.COMPANY_COMPLIANCE]: {
        on: {
          NEXT: State.CAPITAL_DEPOSIT_QUESTIONS,
          PREV: State.EU_BANK_ACCOUNT,
        },
      },

      [State.CAPITAL_DEPOSIT_QUESTIONS]: {
        on: {
          NEXT: State.BENEFICIARIES,
          PREV: State.COMPANY_COMPLIANCE,
        },
      },

      [State.BENEFICIARIES]: {
        on: {
          NEXT: State.PERSONAL_INFORMATION_INTRO,
          PREV: State.CAPITAL_DEPOSIT_QUESTIONS,
        },
      },

      [State.PERSONAL_INFORMATION_INTRO]: {
        entry: shared.actions.trackView(),
        on: {
          NEXT: State.VERIFICATION,
          PREV: {
            actions: shared.actions.toast({
              status: 'error',
              title: locales.toast.definitiveStep,
            }),
          },
        },
      },

      [State.VERIFICATION]: {
        on: {
          NEXT: State.AGREEMENT,
          PREV: State.PERSONAL_INFORMATION_INTRO,
        },
      },

      [State.AGREEMENT]: {
        always: {
          cond: Guard.HasSignedAgreement,
          target: State.SEND_APPLICATION,
        },
        on: {
          NEXT: State.SEND_APPLICATION,
          PREV: State.VERIFICATION,
        },
      },

      [State.SEND_APPLICATION]: {
        invoke: {
          onDone: {
            target: State.SUCCESS,
          },
          onError: {
            actions: shared.actions.error(),
            target: State.VERIFICATION,
          },
          src: Service.SendApplication,
        },
      },

      [State.SUCCESS]: {
        entry: shared.actions.trackView(),
        on: {
          NEXT: State.CAPITAL_DEPOSIT_NEXT_STEPS,
          PREV: State.DONE,
        },
      },

      [State.CAPITAL_DEPOSIT_NEXT_STEPS]: {
        entry: shared.actions.trackView(),
        on: {
          DOWNLOAD_SHINE: State.DOWNLOAD_SHINE_MESSAGE_SENT,
          GO_BENEFICIARIES_DOCS: State.BENEFICIARIES_DOCS,
          PREV: {
            actions: shared.actions.toast({
              status: 'error',
              title: locales.toast.definitiveStep,
            }),
          },
        },
      },

      [State.BENEFICIARIES_DOCS]: {
        entry: shared.actions.trackView(),
        on: {
          PREV: State.CAPITAL_DEPOSIT_NEXT_STEPS,
          'verification-home': State.CAPITAL_DEPOSIT_NEXT_STEPS,
        },
      },

      [State.DOWNLOAD_SHINE_MESSAGE_SENT]: {
        entry: shared.actions.trackView(),
        on: {
          PREV: State.CAPITAL_DEPOSIT_NEXT_STEPS,
        },
      },

      [State.DONE]: {
        data: {
          status: BentoModuleDoneStatus.DONE,
        },
        type: 'final',
      },

      [State.ABORT]: {
        data: {
          status: BentoModuleDoneStatus.ABORT,
        },
        type: 'final',
      },
    },
  },

  {
    actions,
    guards,
    services,
  },
);

export default machine;
